import { useState, useRef, useEffect } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import LoginModal from './login_modal';
import NoticeListModal from './notice_list_modal';

function AppHeader({ onHomeClick }) {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isNoticeListModalOpen, setIsNoticeListModalOpen] = useState(false);
  const desktopSettingsMenuRef = useRef(null);
  const mobileSettingsMenuRef = useRef(null);
  const { user, login, logout, isAdmin, isGuest, role } = useAuth();

  const getRoleLabel = () => {
    if (isAdmin) return '관리자';
    if (isGuest) return '게스트';
    return '';
  };

  // 설정 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDesktopClick = desktopSettingsMenuRef.current && desktopSettingsMenuRef.current.contains(event.target);
      const isMobileClick = mobileSettingsMenuRef.current && mobileSettingsMenuRef.current.contains(event.target);
      
      if (!isDesktopClick && !isMobileClick) {
        setIsSettingsMenuOpen(false);
      }
    };

    if (isSettingsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingsMenuOpen]);

  const handleLogout = () => {
    logout();
    setIsSettingsMenuOpen(false);
  };

  const handleNotice = () => {
    setIsNoticeListModalOpen(true);
    setIsSettingsMenuOpen(false);
  };

  const handleDemoLogin = async () => {
    try {
      await login('demo@test.com', 'demo1234');
    } catch (error) {
      console.error('데모 로그인 오류:', error);
      alert('데모 로그인에 실패했습니다.');
    }
  };

  return (
    <>
      <header className="h-[7vh] min-h-[70px] p-4 md:p-6 flex bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 shadow-xl relative overflow-visible border-b-4 border-orange-800/30 z-50">
        {/* 고급스러운 배경 패턴 - 건물 실루엣 느낌 */}
        <div 
          className="absolute inset-0 opacity-[0.15]" 
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px),
              repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)
            `,
          }}
        ></div>
        
        {/* 우측 상단 장식 요소 */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 blur-2xl"></div>
        
        <div className="relative z-10 flex items-center justify-between w-full">
          <div 
            className="flex items-center gap-3 md:gap-5 cursor-pointer group"
            onClick={onHomeClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onHomeClick?.();
              }
            }}
          >
            {/* 로고 영역 */}
            <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 flex-shrink-0 bg-white/20 rounded-xl backdrop-blur-sm border-2 border-white/40 shadow-lg group-hover:bg-white/30 group-hover:border-white/60 transition-all duration-300 group-hover:scale-105 p-0.5 md:p-1">
              <img 
                src="/1.jpg" 
                alt="IN서면 로고" 
                className="w-full h-full object-contain rounded-md"
              />
            </div>
            
            {/* 브랜드명 영역 */}
            <div className="flex items-center gap-1.5 md:gap-3">
              <h1 className="text-lg md:text-3xl font-extrabold text-white drop-shadow-lg whitespace-nowrap tracking-tight group-hover:scale-105 transition-transform duration-300">
                IN서면
              </h1>
              {/* VISITOR와 DEMO 역할일 때는 "with 다온" 숨김 */}
              {role !== ROLES.VISITOR && role !== ROLES.DEMO && (
                <>
                  <div className="h-5 md:h-8 w-0.5 bg-white/40 rounded-full"></div>
                  <h2 className="text-[0.875rem] md:text-sm px-1.5 md:px-2 py-0.5 md:py-1 text-white/95 font-semibold whitespace-nowrap bg-white/20 rounded-md backdrop-blur-sm border border-white/30" style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>
                    with 다온
                  </h2>
                </>
              )}
            </div>
          </div>
          
          {/* 우측 멘트 및 로그인 버튼 */}
          <div className="hidden md:flex items-center gap-4">
            <span className="text-white/90 text-sm font-medium drop-shadow-md">
              서면 대표 상가 중개인 전용 서비스
            </span>
            
            {user ? (
              <div className="flex items-center gap-2 relative" ref={desktopSettingsMenuRef}>
                <span className="text-white/90 text-sm font-medium">
                  {user.email} ({getRoleLabel()})
                </span>
                <button
                  onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-md transition-colors backdrop-blur-sm border border-white/30 flex items-center gap-1.5"
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  설정
                </button>
                
                {/* 설정 메뉴 드롭다운 */}
                {isSettingsMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl p-1.5 min-w-[140px] z-[200] border border-gray-200">
                    <button
                      onClick={handleNotice}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                      공지사항
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDemoLogin}
                  className="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 text-sm font-bold rounded-md transition-colors border border-orange-200 shadow-sm"
                >
                  데모 체험하기
                </button>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-md transition-colors backdrop-blur-sm border border-white/30"
                >
                  로그인
                </button>
              </div>
            )}
          </div>
          
          {/* 모바일 설정 버튼 */}
          <div className="md:hidden relative" ref={mobileSettingsMenuRef}>
            {user ? (
              <div className="flex items-center gap-1.5">
                <span className="text-white/90 text-[0.875rem] truncate max-w-[60px]">
                  {user.email}
                </span>
                <button
                  onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
                  className="px-1.5 py-0.5 bg-white/20 hover:bg-white/30 text-white text-[0.875rem] rounded border border-white/30 whitespace-nowrap flex items-center gap-1"
                >
                  <svg 
                    className="w-3 h-3" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  설정
                </button>
                
                {/* 모바일 설정 메뉴 드롭다운 */}
                {isSettingsMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl p-1.5 min-w-[120px] z-[200] border border-gray-200">
                    <button
                      onClick={handleNotice}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                      공지사항
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleDemoLogin}
                  className="px-2 py-0.5 bg-orange-100 hover:bg-orange-200 text-orange-700 text-[0.875rem] font-bold rounded border border-orange-200 whitespace-nowrap shadow-sm"
                >
                  데모 체험
                </button>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="px-1.5 py-0.5 bg-white/20 hover:bg-white/30 text-white text-[0.875rem] rounded border border-white/30 whitespace-nowrap"
                >
                  로그인
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      <NoticeListModal 
        isOpen={isNoticeListModalOpen} 
        onClose={() => setIsNoticeListModalOpen(false)} 
      />
    </>
  );
}

export default AppHeader;